/* eslint-disable @typescript-eslint/no-explicit-any */

export interface ChatSearchCriteria {
  checkInDate?: string;
  checkOutDate?: string;
  flexibleDate?: boolean | string;
  lengthOfStay?: number;
  numRooms?: number | string;
  numAdultsPerRoom?: number | string;
  numChildrenPerRoom?: number | string;
  childrenAges?: Array<number | string> | string;
  useRewardsPoints?: boolean;
  clusterCode?: string;
  corporateCode?: string;
  groupCode?: string;
}

export interface FallbackSearchOptions {
  startDate?: string;
  endDate?: string;
  quantity?: number | string;
  childAges?: Array<number | string> | null;
}

export interface FallbackAvailabilityRequest {
  checkInDate?: string;
  checkOutDate?: string;
  flexibleDate?: boolean | string;
  numRooms?: number | string;
  numAdultsPerRoom?: number | string;
  numChildrenPerRoom?: number | string;
  childrenAges?: Array<number | string> | string;
  clusterCode?: string;
  corporateCode?: string;
  groupCode?: string;
}

export interface BuildChatAvailabilityUrlParams {
  baseUrl: string;
  propertyCode: string | number;
  chatSearchCriteria?: ChatSearchCriteria;
  fallbackSearchOptions?: FallbackSearchOptions;
  fallbackAvailabilityRequest?: FallbackAvailabilityRequest;
  fallbackUseRewardsPoints?: boolean;
  fallbackFlexibleDate?: boolean;
  fallbackNumAdultsPerRoom?: number | string;
  forceRateCalendar?: boolean;
}

const toBool = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
  }
  return undefined;
};

const formatDateForUrl = (iso: string): string => {
  const normalized = (iso || '').slice(0, 10);
  const [year, month, day] = normalized.split('-');
  if (year && month && day) return `${month}/${day}/${year}`;
  return iso;
};

const toAgeArray = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value.map(age => parseInt(String(age), 10)).filter(age => !isNaN(age));
  }
  if (typeof value === 'string' && value) {
    return value
      .split(',')
      .map(age => parseInt(String(age), 10))
      .filter(age => !isNaN(age));
  }
  return [];
};

export const buildChatAvailabilityUrl = ({
  baseUrl,
  propertyCode,
  chatSearchCriteria,
  fallbackSearchOptions,
  fallbackAvailabilityRequest,
  fallbackUseRewardsPoints,
  fallbackFlexibleDate,
  fallbackNumAdultsPerRoom,
  forceRateCalendar = false,
}: BuildChatAvailabilityUrlParams): string => {
  const todayISO = new Date().toISOString().slice(0, 10);
  const rawFrom =
    chatSearchCriteria?.checkInDate ??
    fallbackAvailabilityRequest?.checkInDate ??
    fallbackSearchOptions?.startDate ??
    todayISO;

  // For toDate, we need to handle the case where checkOutDate might be undefined
  // (e.g., for flexible date searches). In that case, calculate from checkInDate + lengthOfStay.
  let rawTo =
    chatSearchCriteria?.checkOutDate ??
    fallbackAvailabilityRequest?.checkOutDate ??
    fallbackSearchOptions?.endDate;

  // If no toDate is available, calculate it from fromDate + lengthOfStay
  if (!rawTo) {
    const explicitLengthOfStay = chatSearchCriteria?.lengthOfStay;
    if (explicitLengthOfStay && rawFrom) {
      const fromDateObj = new Date(rawFrom);
      fromDateObj.setDate(fromDateObj.getDate() + explicitLengthOfStay);
      rawTo = fromDateObj.toISOString().slice(0, 10);
    } else {
      // Final fallback to today's date
      rawTo = todayISO;
    }
  }

  const fromDate = formatDateForUrl(rawFrom);
  const toDate = formatDateForUrl(rawTo);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diff = Math.round((new Date(rawTo).getTime() - new Date(rawFrom).getTime()) / msPerDay);
  const lengthOfStay = Math.max(diff, 1);

  const childAgesArray = toAgeArray(
    chatSearchCriteria?.childrenAges ??
      fallbackAvailabilityRequest?.childrenAges ??
      fallbackSearchOptions?.childAges ??
      []
  );
  const childrenCount = Number(
    chatSearchCriteria?.numChildrenPerRoom ?? fallbackAvailabilityRequest?.numChildrenPerRoom ?? childAgesArray.length
  );
  const numberOfRooms = Number(
    chatSearchCriteria?.numRooms ?? fallbackAvailabilityRequest?.numRooms ?? fallbackSearchOptions?.quantity ?? 1
  );
  const numberOfAdults = Number(
    chatSearchCriteria?.numAdultsPerRoom ??
      fallbackAvailabilityRequest?.numAdultsPerRoom ??
      fallbackNumAdultsPerRoom ??
      1
  );
  const useRewards = chatSearchCriteria?.useRewardsPoints ?? fallbackUseRewardsPoints ?? false;
  
  // Determine if this is a flexible date search
  // Priority: chatSearchCriteria.flexibleDate > fallbackAvailabilityRequest.flexibleDate > fallbackFlexibleDate
  // Note: We need to check if chatSearchCriteria.flexibleDate is explicitly set (not undefined)
  // to avoid fallback overriding an explicit false value
  let isFlexible = forceRateCalendar;
  if (!isFlexible) {
    const chatFlexible = toBool(chatSearchCriteria?.flexibleDate);
    // DEBUG: Log the flexible date determination
    console.log('[buildChatAvailabilityUrl] flexibleDate debug:', {
      'chatSearchCriteria?.flexibleDate': chatSearchCriteria?.flexibleDate,
      'chatFlexible (after toBool)': chatFlexible,
      'fallbackAvailabilityRequest?.flexibleDate': fallbackAvailabilityRequest?.flexibleDate,
      'fallbackFlexibleDate': fallbackFlexibleDate,
      'forceRateCalendar': forceRateCalendar,
    });
    if (chatFlexible !== undefined) {
      isFlexible = chatFlexible;
    } else {
      const fallbackRequestFlexible = toBool(fallbackAvailabilityRequest?.flexibleDate);
      if (fallbackRequestFlexible !== undefined) {
        isFlexible = fallbackRequestFlexible;
      } else {
        isFlexible = fallbackFlexibleDate ?? false;
      }
    }
  }
  console.log('[buildChatAvailabilityUrl] Final isFlexible:', isFlexible);

  const params = new URLSearchParams({
    propertyCode: String(propertyCode),
    fromDate,
    toDate,
    flexibleDateSearch: isFlexible ? 'true' : 'false',
    lengthOfStay: String(lengthOfStay),
    numberOfRooms: String(isNaN(numberOfRooms) ? 1 : numberOfRooms),
    numberOfAdults: String(isNaN(numberOfAdults) ? 1 : numberOfAdults),
    childrenCount: String(isNaN(childrenCount) ? 0 : childrenCount),
    childrenAges: childAgesArray.join(','),
    useRewardsPoints: useRewards ? 'true' : 'false',
  });

  if (isFlexible) {
    params.set('isRateCalendar', 'true');
    params.set('flexibleDateSearch', 'true');
  }

  if (!useRewards) {
    const clusterCode = chatSearchCriteria?.clusterCode ?? fallbackAvailabilityRequest?.clusterCode;
    const corporateCode = chatSearchCriteria?.corporateCode ?? fallbackAvailabilityRequest?.corporateCode;
    const groupCode = chatSearchCriteria?.groupCode ?? fallbackAvailabilityRequest?.groupCode;

    if (corporateCode || clusterCode === 'corp') {
      params.set('clusterCode', 'corp');
      if (corporateCode) params.set('corporateCode', corporateCode);
    } else if (clusterCode === 'AAA' || clusterCode === 'aaa') {
      params.set('clusterCode', 'aaa');
    } else if (clusterCode === 'GOV' || clusterCode === 'gov') {
      params.set('clusterCode', 'gov');
    } else if (clusterCode === 'S9R' || clusterCode === 's9r') {
      params.set('clusterCode', 'S9R');
    } else if (groupCode) {
      params.set('clusterCode', 'grp');
      params.set('groupCode', groupCode);
    }
  }

  return `${baseUrl}?${params.toString()}`;
};

export interface ChatFilterState {
  brands?: string[];
  amenities?: string[];
  hotelTypes?: string[];
  activities?: string[];
  transportationTypes?: string[];
  priceRange?: { min?: number; max?: number };
  pointsRange?: { min?: number; max?: number };
  distance?: number;
  events?: string[];
  states?: string[];
  cities?: string[];
}

export interface BuildFindHotelsUrlParams {
  baseUrl: string;
  placeId?: string;
  latitude?: number;
  longitude?: number;
  locationName?: string;
  city?: string;
  stateProvince?: string;
  country?: string;
  address?: string;
  region?: string;
  chatSearchCriteria?: ChatSearchCriteria;
  filterState?: ChatFilterState;
}

const formatDateForFindHotels = (iso: string | undefined): string => {
  if (!iso) return '';
  const normalized = (iso || '').slice(0, 10);
  const [year, month, day] = normalized.split('-');
  if (year && month && day) return `${month}/${day}/${year}`;
  return iso;
};

/**
 * Builds a Find Hotels URL with all search criteria from the conversation state.
 * This is used by the "Search All Properties" button in OCard.
 *
 * Example URL format:
 * /search/findHotels.mi?fromToDate_submit=04/29/2026&fromDate=04/28/2026&toDate=04/29/2026
 * &toDateDefaultFormat=04/29/2026&fromDateDefaultFormat=04/28/2026&flexibleDateSearch=false
 * &t-start=04/28/2026&t-end=04/29/2026&lengthOfStay=1&childrenCountBox=2+Children+Per+Room
 * &childrenCount=2&childrenAges=1,2&clusterCode=none&useRewardsPoints=true&isSearch=true...
 */
export const buildFindHotelsUrl = ({
  baseUrl,
  placeId,
  latitude,
  longitude,
  locationName,
  city,
  stateProvince,
  country,
  address,
  region,
  chatSearchCriteria,
  filterState,
}: BuildFindHotelsUrlParams): string => {
  const params = new URLSearchParams();

  // Date parameters
  const fromDate = formatDateForFindHotels(chatSearchCriteria?.checkInDate);
  const toDate = formatDateForFindHotels(chatSearchCriteria?.checkOutDate);

  // Calculate length of stay
  let lengthOfStay = 1;
  if (chatSearchCriteria?.checkInDate && chatSearchCriteria?.checkOutDate) {
    const msPerDay = 1000 * 60 * 60 * 24;
    const diff = Math.round(
      (new Date(chatSearchCriteria.checkOutDate).getTime() - new Date(chatSearchCriteria.checkInDate).getTime()) /
        msPerDay
    );
    lengthOfStay = Math.max(diff, 1);
  }

  // Flexible date flag
  const isFlexible = toBool(chatSearchCriteria?.flexibleDate) ?? false;

  // Date-related parameters (matching the example URL format)
  if (fromDate && toDate) {
    params.set('fromToDate_submit', toDate);
    params.set('fromDate', fromDate);
    params.set('toDate', toDate);
    params.set('toDateDefaultFormat', toDate);
    params.set('fromDateDefaultFormat', fromDate);
    params.set('flexibleDateSearch', isFlexible ? 'true' : 'false');
    params.set('t-start', fromDate);
    params.set('t-end', toDate);
    params.set('lengthOfStay', String(lengthOfStay));
  }

  // Rooms and guests
  const numberOfRooms = Number(chatSearchCriteria?.numRooms ?? 1);
  const numberOfAdults = Number(chatSearchCriteria?.numAdultsPerRoom ?? 1);
  const childrenCount = Number(chatSearchCriteria?.numChildrenPerRoom ?? 0);
  const childAgesArray = toAgeArray(chatSearchCriteria?.childrenAges ?? []);

  // Children parameters
  if (childrenCount > 0) {
    const childrenLabel = childrenCount === 1 ? 'Child' : 'Children';
    params.set('childrenCountBox', `${childrenCount} ${childrenLabel} Per Room`);
    params.set('childrenCount', String(childrenCount));
    if (childAgesArray.length > 0) {
      params.set('childrenAges', childAgesArray.join(','));
    }
  }

  // Special rates / cluster code
  const useRewards = chatSearchCriteria?.useRewardsPoints ?? false;
  const clusterCode = chatSearchCriteria?.clusterCode;
  const corporateCode = chatSearchCriteria?.corporateCode;
  const groupCode = chatSearchCriteria?.groupCode;

  if (!useRewards) {
    if (corporateCode || clusterCode === 'corp') {
      params.set('clusterCode', 'corp');
      if (corporateCode) params.set('corporateCode', corporateCode);
    } else if (clusterCode === 'AAA' || clusterCode === 'aaa') {
      params.set('clusterCode', 'aaa');
    } else if (clusterCode === 'GOV' || clusterCode === 'gov') {
      params.set('clusterCode', 'gov');
    } else if (clusterCode === 'S9R' || clusterCode === 's9r') {
      params.set('clusterCode', 'S9R');
    } else if (groupCode) {
      params.set('clusterCode', 'grp');
      params.set('groupCode', groupCode);
    } else {
      params.set('clusterCode', 'none');
    }
  } else {
    params.set('clusterCode', 'none');
  }

  // Rewards points
  params.set('useRewardsPoints', useRewards ? 'true' : 'false');

  // Search flags
  params.set('isAdvanceSearch', 'false');
  params.set('isNGSF', 'true');
  params.set('recordsPerPage', '20');

  // Location parameters - destination address type
  params.set('destinationAddress.type', 'City');

  // Latitude/Longitude
  if (latitude) {
    params.set('destinationAddress.latitude', String(latitude));
  }
  if (longitude) {
    params.set('destinationAddress.longitude', String(longitude));
  }

  // Search type and internal flags
  params.set('isInternalSearch', 'true');
  params.set('vsInitialRequest', 'false');
  params.set('searchType', 'InCity');

  // State/Province
  if (stateProvince) {
    params.set('destinationAddress.stateProvinceDisplayName', stateProvince);
    params.set('destinationAddress.stateProvince', stateProvince);
  }

  // Country
  if (country) {
    params.set('countryName', country);
    params.set('destinationAddress.country', country);
  }

  // Search radius
  params.set('searchRadius', '50');
  params.set('singleSearchAutoSuggest', 'Unmatched');

  // Place ID
  if (placeId) {
    params.set('destinationAddress.placeId', placeId);
  }

  params.set('for-hotels-nearme', 'Near');

  // Address
  if (address) {
    params.set('destinationAddress.address', address);
  }

  // UI state flags
  params.set('collapseAccordian', 'is-hidden');
  params.set('singleSearch', 'true');

  // Secondary text (state, country)
  if (stateProvince && country) {
    params.set('destinationAddress.secondaryText', `${stateProvince}, ${country}`);
  }

  // City
  if (city) {
    params.set('destinationAddress.city', city);
    params.set('destinationAddress.mainText', city);
  }

  // Transient flag
  params.set('isTransient', 'true');

  // Initial request flags
  params.set('initialRequest', 'false');
  params.set('flexibleDateSearchRateDisplay', 'false');
  params.set('isSearch', 'true');
  params.set('isRateCalendar', isFlexible ? 'true' : 'false');

  // Destination (combined location name)
  if (locationName) {
    params.set('destinationAddress.destination', locationName);
  } else if (city && stateProvince) {
    params.set('destinationAddress.destination', `${city}, ${stateProvince}`);
  } else if (city) {
    params.set('destinationAddress.destination', city);
  }

  // Region parameter - only set if explicitly provided (for leisure region searches)
  if (region) {
    params.set('destinationAddress.region', region);
  }

  // Hide flexible date calendar
  params.set('isHideFlexibleDateCalendar', 'false');

  // Room count with label
  const roomLabel = numberOfRooms === 1 ? 'Room' : 'Rooms';
  params.set('roomCountBox', `${numberOfRooms} ${roomLabel}`);
  params.set('roomCount', String(numberOfRooms));

  // Guest count with label
  const adultLabel = numberOfAdults === 1 ? 'Adult' : 'Adults';
  params.set('guestCountBox', `${numberOfAdults} ${adultLabel} Per Room`);
  params.set('numAdultsPerRoom', String(numberOfAdults));

  // Filter parameters - matching the shop URL format
  if (filterState?.amenities && filterState.amenities.length > 0) {
    params.set('amenities', filterState.amenities.join(','));
  }
  if (filterState?.brands && filterState.brands.length > 0) {
    params.set('marriottBrands', filterState.brands.join(','));
  }
  if (filterState?.hotelTypes && filterState.hotelTypes.length > 0) {
    params.set('hotelType', filterState.hotelTypes.join(','));
  }
  if (filterState?.activities && filterState.activities.length > 0) {
    params.set('activities', filterState.activities.join(','));
  }
  if (filterState?.transportationTypes && filterState.transportationTypes.length > 0) {
    params.set('transportation', filterState.transportationTypes.join(','));
  }
  if (filterState?.priceRange?.min !== undefined || filterState?.priceRange?.max !== undefined) {
    const priceMin = filterState.priceRange?.min ?? 0;
    const priceMax = filterState.priceRange?.max ?? 9999;
    params.set('price', `${priceMin}-${priceMax}`);
  }
  if (filterState?.pointsRange?.min !== undefined || filterState?.pointsRange?.max !== undefined) {
    const pointsMin = filterState.pointsRange?.min ?? 0;
    const pointsMax = filterState.pointsRange?.max ?? 999999;
    params.set('points', `${pointsMin}-${pointsMax}`);
  }
  if (filterState?.distance !== undefined) {
    params.set('distance', String(filterState.distance));
  }
  if (filterState?.events && filterState.events.length > 0) {
    params.set('events', filterState.events.join(','));
  }
  // States and cities filter parameters
  if (filterState?.states && filterState.states.length > 0) {
    params.set('states', filterState.states.join(','));
  }
  if (filterState?.cities && filterState.cities.length > 0) {
    params.set('cities', filterState.cities.join(','));
  }

  // View mode - default to list for Search All Properties
  params.set('view', 'list');
  params.set('deviceType', 'desktop-web');

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};
